// utils/googleMaps.js
import apiPath from "../utils/pathObj";
import { apiGet } from "../utils/apiFetch";

let googleMapsPromise = null;
let apiKeyPromise = null;

// Fetch API key from server (cached)
const fetchGoogleMapsApiKey = async () => {
  if (apiKeyPromise) return apiKeyPromise;
  
  apiKeyPromise = new Promise(async (resolve, reject) => {
    try {
      const response = await apiGet(apiPath.getGoogleMapsKey);
      if (response?.data?.success && response?.data?.results?.apiKey) {
        resolve(response.data.results.apiKey);
      } else {
        reject(new Error('Failed to fetch Google Maps API key'));
      }
    } catch (error) {
      console.error('Error fetching Google Maps API key:', error);
      reject(error);
    }
  });
  
  return apiKeyPromise;
};

// Shared singleton loader
export const loadGoogleMaps = async () => {
  // Check if already loaded
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google);
  }

  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise(async (resolve, reject) => {
    try {
      // 1. Fetch API key securely from server
      const apiKey = await fetchGoogleMapsApiKey();
      
      // 2. Remove any existing script tags to prevent duplicate loading
      const existingScript = document.getElementById('google-maps-script');
      if (existingScript) {
        existingScript.remove();
      }

      // 3. Create and inject the script with both 'places' and 'maps' libraries
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      // Important: We load 'places' (for new PlaceAutocompleteElement) and 'maps' (for map rendering)
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,maps&v=weekly`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        // 4. Wait for the new PlaceAutocompleteElement to be registered by Google
        // The new web component isn't immediately available on script load, so we use a small timeout/detection loop
        const checkElement = () => {
          if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
            resolve(window.google);
          } else {
            setTimeout(checkElement, 100);
          }
        };
        checkElement();
      };
      
      script.onerror = (err) => {
        googleMapsPromise = null; // allow retry on next call
        reject(err);
      };
      
      document.head.appendChild(script);
    } catch (error) {
      googleMapsPromise = null;
      reject(error);
    }
  });

  return googleMapsPromise;
};

// Geocode an address to get latitude/longitude
export const geocodeAddress = async (address) => {
  try {
    const google = await loadGoogleMaps();
    const geocoder = new google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            latitude: location.lat(),
            longitude: location.lng(),
            formattedAddress: results[0].formatted_address,
            placeId: results[0].place_id,
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
};

// Get place details from Google Places API
export const getPlaceDetails = async (placeId) => {
  try {
    const google = await loadGoogleMaps();
    const place = new google.maps.places.Place({
      id: placeId,
      requestedLanguage: 'en',
    });

    await place.fetchFields({
      fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
    });

    return {
      name: place.displayName,
      formattedAddress: place.formattedAddress,
      latitude: place.location?.lat(),
      longitude: place.location?.lng(),
      addressComponents: place.addressComponents,
      placeId: place.id,
    };
  } catch (error) {
    console.error('Place details error:', error);
    throw error;
  }
};